import React, {useId, useState} from 'react';
import {
  Select,
  MenuItem,
  SelectChangeEvent,
  Typography,
  Box,
  FormControl,
  OutlinedInput,
  InputLabel,
} from '@mui/material';
import AurCountryFlag from '../../../common/aur-country-flag/AurCountryFlag';
import {t} from 'i18next';

export interface ServiceAgreementProps {
  geography: string;
  countryName: string;
  engagementName: string;
  engagementCode?: string;
  engagementType: string;
  id: string;
  serviceType: string;
  eciAccessEnabled?: boolean;
  reviewPlatform?: string;
}

interface Props {
  options: ServiceAgreementProps[];
  value: string | undefined;
  onChange: (value: string) => void;
  label?: string;
  valueLabel?: string;
  hintText?: string;
  keepDisabledFocusable?: boolean;
}

const AurServiceAgreementDropdown: React.FC<Props> = ({
  options,
  value,
  onChange,
  label = t('projectCreation.serviceAgreementDropdown.dropdownLabel'),
  valueLabel,
  hintText,
  keepDisabledFocusable = false,
}) => {
  const hintId = `aur-select-hint-${useId().replace(/:/g, '')}`;
  const isNativeDisabled = options.length < 1 && !keepDisabledFocusable;
  const isDisabled = options.length < 1;
  const [open, setOpen] = useState(false);
  let disabledClassName = '';

  if (isDisabled) {
    disabledClassName = keepDisabledFocusable
      ? 'custom-select-focusable-disabled'
      : 'custom-select-disabled-background';
  }

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  const renderSelectedValue = (selectedId: string) => {
    const selected = options.find((opt) => opt.id === selectedId);
    if (!selected) return '';

    return (
      <Box display="flex" alignItems="center" gap={1} className="width100P">
        <Typography className="typo-text label2_size selected-flag">
          <AurCountryFlag
            countryCode={selected.geography}
            altText={''}
          />
        </Typography>
        <Typography className="typo-text label2_size selected-value">
          {` ${selected.countryName} - [${selected.engagementName}]`}
        </Typography>
      </Box>
    );
  };

  return (
    <FormControl
      fullWidth
      className="aur-service-agreement-dropdown selectFormControl"
    >
      <span id={hintId} style={{display: 'none'}}>
        {hintText ?? t('common.dropdown.optionSelectHintsText')}
      </span>
      <InputLabel
        shrink={Boolean(value)}
        className={`select-placeholder typo-text body2_size ${disabledClassName}`}
        id="service-agreement-label"
      >
        {value
          ? (valueLabel ??
            t('projectCreation.serviceAgreementDropdown.dropdownSelectedLabel'))
          : label}
      </InputLabel>
      <Select
        labelId="service-agreement-label"
        displayEmpty
        disabled={isNativeDisabled}
        aria-disabled={keepDisabledFocusable ? isDisabled : undefined}
        open={open}
        onOpen={() => {
          if (!(keepDisabledFocusable && isDisabled)) {
            setOpen(true);
          }
        }}
        onClose={() => setOpen(false)}
        onKeyDown={(e) => {
          if (
            keepDisabledFocusable &&
            isDisabled &&
            (e.key === 'Enter' ||
              e.key === ' ' ||
              e.key === 'ArrowDown' ||
              e.key === 'ArrowUp')
          ) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        value={value}
        onChange={handleChange}
        input={<OutlinedInput notched={false} label="" />}
        className={`custom-select select-root ${disabledClassName}`}
        aria-describedby={hintId}
        renderValue={(selected) =>
          selected ? (
            <Box display="flex" alignItems="center" gap={1}>
              {renderSelectedValue(selected)}
            </Box>
          ) : (
            ''
          )
        }
      >
        {options.map((option) => (
          <MenuItem
            key={option.id}
            value={option.id}
            className="menu-item-service-agreement"
          >
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Box className="item-country">
                <Typography className="typo-text body4_size">
                  <AurCountryFlag
                    countryCode={option.geography}
                    altText={option.countryName}
                  />
                </Typography>
                <Typography className="typo-text body4_size">
                  {` ${option.countryName}`}
                </Typography>
              </Box>

              <Box display="flex">
                <Typography className="item-name typo-text.subhead4_size">
                  [{option.engagementName}]
                  {option.engagementCode ? ` | ${option.engagementCode}` : ''}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default AurServiceAgreementDropdown;
